import { maybe, type Maybe } from '@passionware/monads';
import { AnimatePresence, useIsPresent } from 'motion/react';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from 'v5/platform/dom/cn';
import { StackSlot } from './StackSlot';

/*
 * Sheets over a content card, opened from anywhere in the tree.
 *
 * The layout owns the stack: where the sheets stand (docked to the card's right edge, in the order
 * they opened), how they come and go (`StackSlot`), and how each behaves: a non-modal dialog with
 * a label, Escape, the focus handed back on the way out. A caller owns the content:
 * `<ContentSheet open …>` registers a slot while open and portals its children into that slot's
 * element, so the content keeps the caller's React tree: its contexts, boundaries and state.
 *
 * Two registries, on purpose: the records drive which slots the stack renders, the elements say
 * which slots are in the document. A closing sheet's record goes at once, its element stays until
 * the slot has slid out, and the caller keeps its last content in it that long.
 */

export interface SheetRecord {
  id: string;
  /** What the sheet is, for a screen reader: "Resource details". */
  label: string;
  /** Escape, with the focus in the sheet. Without it the sheet has no Escape of its own. */
  onClose?: () => void;
}

interface SheetStackApi {
  /** Adds the sheet, or updates it; a record equal to the stored one changes nothing. */
  register: (record: SheetRecord) => void;
  unregister: (id: string) => void;
  setElement: (id: string, element: HTMLElement | null) => void;
}

export interface SheetStackState {
  records: SheetRecord[];
  /** The slots in the document, by sheet id; a leaving slot's too, until it is gone. */
  elements: Record<string, HTMLElement>;
  api: SheetStackApi;
}

const SheetStackApiContext = createContext<Maybe<SheetStackApi>>(null);
const SheetStackElementsContext = createContext<Record<string, HTMLElement>>(
  {}
);

const sameRecord = (a: SheetRecord, b: SheetRecord) =>
  a.id === b.id && a.label === b.label && a.onClose === b.onClose;

/** The stack's state, for the layout that renders it and hands it to `SheetStackProvider`. */
export function useSheetStack(): SheetStackState {
  const [records, setRecords] = useState<SheetRecord[]>([]);
  const [elements, setElements] = useState<Record<string, HTMLElement>>({});
  // one api for the stack's lifetime: a sheet registers on every render, and the same functions
  // keep that from rippling into renders of its own
  const [api] = useState<SheetStackApi>(() => ({
    register: record =>
      setRecords(prev => {
        const stored = prev.find(r => r.id === record.id);
        if (maybe.isAbsent(stored)) return [...prev, record];
        if (sameRecord(stored, record)) return prev;
        return prev.map(r => (r.id === record.id ? record : r));
      }),
    unregister: id =>
      setRecords(prev =>
        prev.some(r => r.id === id) ? prev.filter(r => r.id !== id) : prev
      ),
    setElement: (id, element) =>
      setElements(prev => {
        if (element) {
          return prev[id] === element ? prev : { ...prev, [id]: element };
        }
        if (!(id in prev)) return prev;
        const { [id]: _gone, ...rest } = prev;
        return rest;
      })
  }));
  return { records, elements, api };
}

/**
 * Around everything that may open a sheet, the card's content and the column beside it alike:
 * what a `ContentSheet` there needs of the stack.
 */
export function SheetStackProvider({
  stack,
  children
}: {
  stack: SheetStackState;
  children: ReactNode;
}) {
  return (
    <SheetStackApiContext.Provider value={stack.api}>
      <SheetStackElementsContext.Provider value={stack.elements}>
        {children}
      </SheetStackElementsContext.Provider>
    </SheetStackApiContext.Provider>
  );
}

/**
 * The slots, docked to the right edge of the card that renders this, one per registered sheet in
 * the order they opened. Takes no pointer itself: the content under its empty part stays
 * clickable. `isolate`: a leaving slot is pinned under the sheets that stay (`z-index: -1`)
 * inside this layer, not under the page beneath it.
 */
export function SheetStack({
  records,
  beside = false,
  flush = false,
  className
}: {
  records: SheetRecord[];
  /** The sheets stand beside the content, in room it left them, not over it: no shadow cast. */
  beside?: boolean;
  /** Inside a frame of its own (a dialog): the panels meet its edges, square, with no frame of theirs there. */
  flush?: boolean;
  /** Where it stands; it fills its positioned parent by default. */
  className?: string;
}) {
  return (
    // `@container`: a panel reads the card's width (`cqw`) to round the edge it sticks out with.
    // `drop-shadow`: one shadow for all the sheets, cast from the leftmost edge of what they paint;
    // the panels draw none. `clip-path`: the shadow stays inside the card's top, right and bottom
    // edges, round at its right corners as the card is, and the left stays open for a sheet wider
    // than the card
    <div
      data-sheet-stack
      data-beside={beside ? '' : undefined}
      data-flush={flush ? '' : undefined}
      className={cn(
        'pointer-events-none absolute inset-0 isolate flex justify-end @container drop-shadow-sheet-edge [clip-path:inset(0_0_0_-100vw_round_0_var(--radius-xl)_var(--radius-xl)_0)] *:pointer-events-auto',
        beside && 'drop-shadow-none',
        className
      )}
    >
      <AnimatePresence initial={false}>
        {records.map(record => (
          <SheetSlot
            key={record.id}
            record={record}
            beside={beside && !flush}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

/** One slot: a non-modal dialog whose content the caller portals in. Escape closes it. */
function SheetSlot({
  record,
  beside
}: {
  record: SheetRecord;
  beside: boolean;
}) {
  // A sheet leaving keeps the look it had beside the card (its round left edge): the card takes
  // the room back the moment it closes, which would make the stack's `beside` false mid-exit.
  const isPresent = useIsPresent();
  const [besideShown, setBesideShown] = useState(beside);
  if (isPresent && besideShown !== beside) setBesideShown(beside);
  const api = maybe.getOrThrow(
    useContext(SheetStackApiContext),
    'SheetStack renders inside its provider'
  );
  const elementRef = useRef<HTMLDivElement | null>(null);
  // told once for the slot's lifetime, not through a callback ref that would fire on re-renders
  useLayoutEffect(() => {
    api.setElement(record.id, elementRef.current);
    return () => api.setElement(record.id, null);
  }, [api, record.id]);
  useSheetFocus(elementRef);
  useSheetRoom(elementRef);
  useSheetEscape(elementRef, record.onClose);

  return (
    <StackSlot
      ref={elementRef}
      data-beside={besideShown ? '' : undefined}
      slideFrom="right"
      role="dialog"
      aria-label={record.label}
      // the fallback for content with nothing to focus; content with a field takes it itself
      tabIndex={-1}
      className="h-full outline-none"
    />
  );
}

/** Kept free at the window's edge. */
const GUTTER_PX = 16;

/**
 * Where what the sheet shows ends at the left: inside the padding of the nearest ancestor that
 * clips (a page's content area), so it keeps the inset the card has, or the window's edge, less a
 * gutter.
 */
const visibleLeftOf = (element: HTMLElement) => {
  for (
    let ancestor = element.parentElement;
    ancestor && ancestor !== document.body;
    ancestor = ancestor.parentElement
  ) {
    const style = getComputedStyle(ancestor);
    if (style.overflowX !== 'visible') {
      return Math.max(
        0,
        ancestor.getBoundingClientRect().left +
          ancestor.clientLeft +
          parseFloat(style.paddingLeft)
      );
    }
  }
  return GUTTER_PX;
};

/**
 * The room a sheet has, from its right edge to where it can still be seen, in `--sheet-room`: a
 * panel wider than its card runs out to the card's left, but not out of sight (`SheetPanel`).
 * The right edge by offsets, not the painted box: the slot slides in by transform.
 */
function useSheetRoom(elementRef: { current: HTMLDivElement | null }) {
  useLayoutEffect(() => {
    const element = elementRef.current;
    const stack = element?.offsetParent;
    if (!element || !(stack instanceof HTMLElement)) return;
    const measure = () => {
      const right =
        stack.getBoundingClientRect().left +
        element.offsetLeft +
        element.offsetWidth;
      element.style.setProperty(
        '--sheet-room',
        `${Math.max(0, right - visibleLeftOf(element))}px`
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.documentElement);
    observer.observe(stack);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [elementRef]);
}

/**
 * Escape from anywhere in the sheet's DOM. A native listener: the content is portaled in from
 * the caller's tree, so React's events from it bubble to the caller, not to the slot. Stopped
 * here, it closes the sheet and nothing around it in the DOM; React's handlers up the caller's
 * tree still see it, so a caller with an Escape of its own checks the key came from its own DOM
 * (`SplitViewColumn`). Radix layers around the layout (a
 * dialog) listen in the capture phase, before this: such a host holds its Escape back itself
 * (`onEscapeKeyDown`) while a sheet is open. A popover or menu inside is portaled out of the
 * slot: its Escape is its own.
 */
function useSheetEscape(
  elementRef: { current: HTMLDivElement | null },
  onClose: (() => void) | undefined
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const close = onCloseRef.current;
      if (event.key !== 'Escape' || event.defaultPrevented || !close) return;
      event.stopPropagation();
      close();
    };
    element.addEventListener('keydown', onKeyDown);
    return () => element.removeEventListener('keydown', onKeyDown);
  }, [elementRef]);
}

/**
 * The focus goes into the sheet when it opens, and back to where it came from when the sheet
 * closes, but only if it was still inside: a sheet closed by a click on a row leaves the focus on
 * that row.
 */
function useSheetFocus(elementRef: { current: HTMLDivElement | null }) {
  const openerRef = useRef<HTMLElement | null>(null);
  const isPresent = useIsPresent();

  // on mount, and again when the sheet is opened back while its slot still slides out
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !isPresent) return;
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement ? active : null;
    if (!element.contains(active)) {
      element.focus({ preventScroll: true });
    }
  }, [elementRef, isPresent]);

  // at once when the sheet starts to leave, not after its slide: the next key belongs elsewhere
  useEffect(() => {
    if (isPresent) return;
    giveFocusBack(elementRef.current, openerRef.current);
  }, [isPresent, elementRef]);

  // a sheet taken away without a slide (its caller unmounted): the same, before the node goes
  useLayoutEffect(() => {
    return () => giveFocusBack(elementRef.current, openerRef.current);
  }, [elementRef]);
}

function giveFocusBack(
  element: HTMLElement | null,
  opener: HTMLElement | null
) {
  if (!element?.contains(document.activeElement)) return;
  if (opener && opener.isConnected) {
    opener.focus({ preventScroll: true });
  }
}

export interface ContentSheetProps {
  open: boolean;
  label: string;
  onClose?: () => void;
  /** The panel, drawn by the caller (`SheetPanel` or its own). Absent while closed is fine. */
  children?: ReactNode;
}

/**
 * A sheet over the content card, rendered from wherever its state lives (the column beside the
 * card, say). Renders nothing in place: while `open`, its children stand in the card's sheet
 * stack, in the caller's own React tree. On the way out the last children stay until the slot has
 * slid away.
 */
export function ContentSheet({
  open,
  label,
  onClose,
  children
}: ContentSheetProps) {
  const api = maybe.getOrThrow(
    useContext(SheetStackApiContext),
    'ContentSheet renders inside a layout with a sheet stack'
  );
  const elements = useContext(SheetStackElementsContext);
  const id = useId();
  const element = elements[id];
  // the content the leaving slot keeps: what was in it when the sheet closed
  const heldRef = useRef<ReactNode>(null);
  if (open) {
    heldRef.current = children;
  }
  // opened back while the slot still slides out: the content mounts afresh, as in a new slot,
  // with its own focus and state, not the leaving one's
  const [generation, setGeneration] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setGeneration(count => count + 1);
  }

  useLayoutEffect(() => {
    if (open) {
      api.register({ id, label, onClose });
    } else {
      api.unregister(id);
    }
  });
  useLayoutEffect(() => () => api.unregister(id), [api, id]);

  return maybe.map(element, element =>
    createPortal(open ? children : heldRef.current, element, String(generation))
  );
}
